import React, { useContext, useState, useEffect, useRef } from 'react';
import dayjs from 'utils/dayjs';
import styled from 'styled-components';

import { divide } from '../../utils';
import { Table, Input, Form } from 'antd';

const EditableContext = React.createContext();

const IndentedSummaryCell = styled(Table.Summary.Cell)`
    padding-left: 2.8em !important;
`;

const EditableRow = ({ index, ...props }) => {
    const [form] = Form.useForm();
    return (
        <Form form={form} component={false}>
            <EditableContext.Provider value={form}>
                <tr {...props} />
            </EditableContext.Provider>
        </Form>
    );
};
const EditableCell = ({ title, editable, children, dataIndex, record, handleSave, ...restProps }) => {
    const [editing, setEditing] = useState(false);
    const inputRef = useRef();
    const form = useContext(EditableContext);
    useEffect(() => {
        if (editing) {
            inputRef.current.focus();
        }
    }, [editing]);

    const toggleEdit = () => {
        setEditing(!editing);
        form.setFieldsValue({
            [dataIndex]: record[dataIndex],
        });
    };

    const save = async () => {
        try {
            const values = await form.validateFields();
            toggleEdit();
            handleSave({ ...record, ...values });
        } catch (errInfo) {
            // eslint-disable-next-line no-console
            console.log('Save failed:', errInfo);
        }
    };

    let childNode = children;

    if (editable) {
        childNode = editing ? (
            <Form.Item
                style={{
                    margin: 0,
                }}
                name={dataIndex}
                rules={[
                    {
                        required: true,
                        message: `${title} is required.`,
                    },
                ]}
            >
                <Input ref={inputRef} type="number" onPressEnter={save} onBlur={save} />
            </Form.Item>
        ) : (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <div
                className="gd-editable-cell-value-wrap"
                role="textbox"
                tabIndex="0"
                style={{
                    paddingRight: '2em',
                }}
                onClick={toggleEdit}
            >
                {children}
            </div>
        );
    }

    return <td {...restProps}>{childNode}</td>;
};

const ForecastTable = ({ formData, hotelSize, onForecastRowChange }) => {
    const cols = [
        {
            title: 'Dates',
            width: '12%',
            dataIndex: 'date',
            render: (date) => dayjs(date).format('DD/MM/YYYY'),
        },
        {
            title: 'Forecast rooms (without group)',
            dataIndex: 'forecast',
            width: '20%',
            editable: true,
        },
        {
            title: 'Number of Group Rooms',
            dataIndex: 'groupSize',
            width: '20%',
            editable: true,
        },
        {
            title: 'Number of Comp Rooms inc. in group total',
            width: '20%',
            dataIndex: 'compRooms',
            editable: true,
        },
        {
            title: 'Displaced Rooms',
            width: '10%',
            dataIndex: 'displacedRooms',
        },
    ];

    const handleSave = (row) => {
        const availableRooms = parseFloat(hotelSize) - parseFloat(row.forecast);
        let displacedRooms = parseFloat(row.groupSize) - availableRooms;
        if (formData.groupWash) {
            const washRooms = Math.ceil(row.groupSize * divide(formData.groupWashPercent, 100));
            displacedRooms -= washRooms;
        }
        displacedRooms = displacedRooms < 0 ? 0 : displacedRooms;
        onForecastRowChange({
            ...row,
            displacedRooms,
        });
    };

    const components = {
        body: {
            row: EditableRow,
            cell: EditableCell,
        },
    };
    const columns = cols.map((col) => {
        if (!col.editable) {
            return col;
        }

        return {
            ...col,
            onCell: (record) => ({
                ...col,
                record,
                handleSave,
            }),
        };
    });

    let totalRooms = 0;
    let totalGroupRooms = 0;
    let totalCompRooms = 0;
    let totalDisplacedRooms = 0;
    formData.forecasts.forEach((forecast) => {
        totalRooms += parseInt(forecast.forecast, 10);
        totalGroupRooms += parseInt(forecast.groupSize, 10);
        totalCompRooms += parseInt(forecast.compRooms, 10);
        totalDisplacedRooms += parseInt(forecast.displacedRooms, 10);
    });

    return (
        <div>
            <Table
                components={components}
                rowClassName="gd-editable-row"
                bordered
                dataIndex="date"
                dataSource={formData.forecasts}
                columns={columns}
                pagination={false}
                summary={() =>
                    formData.forecasts.length > 1 && (
                        <Table.Summary.Row>
                            <Table.Summary.Cell>Total</Table.Summary.Cell>
                            <IndentedSummaryCell>{totalRooms}</IndentedSummaryCell>
                            <IndentedSummaryCell>{totalGroupRooms}</IndentedSummaryCell>
                            <IndentedSummaryCell>{totalCompRooms}</IndentedSummaryCell>
                            <Table.Summary.Cell>{totalDisplacedRooms}</Table.Summary.Cell>
                        </Table.Summary.Row>
                    )
                }
            />
        </div>
    );
};

export default ForecastTable;
